/* import __COLOCATED_TEMPLATE__ from './connect-button.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import ConnectButtonDropdownItem from './connect-button-dropdown-item';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';

export interface ConnectButtonArgs {
  isDisabled?: boolean;
  selectedNumberConfigurationPhoneNumber: string | undefined;
  selectConfig: (phoneNumber: string | undefined) => void;
  connect: () => Promise<void>;
}

interface Signature {
  Args: ConnectButtonArgs;
  Element: HTMLElement;
}

export default class ConnectButton extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  get isDisabled() {
    return this.args.isDisabled || !this.hasLoadedNumberConfigurations;
  }

  get numberConfigurations() {
    return this.finVoiceConfigService.numberConfigurations;
  }

  get hasLoadedNumberConfigurations() {
    return this.finVoiceConfigService.numberConfigurationsLoaded;
  }

  get phoneNumbers() {
    return this.finVoiceConfigService.phoneNumbers;
  }

  get buttonLabel() {
    if (!this.args.selectedNumberConfigurationPhoneNumber) {
      return this.intl.t('ai-agent.voice-playground.connect');
    }

    return this.intl.t('ai-agent.voice-playground.test-configuration', {
      label:
        this.phoneNumbers[this.args.selectedNumberConfigurationPhoneNumber].name ||
        this.args.selectedNumberConfigurationPhoneNumber,
    });
  }

  get hasNumberConfigurations() {
    return Object.keys(this.numberConfigurations).length > 0;
  }

  get shouldShowDropdown() {
    return this.hasNumberConfigurations || !this.hasLoadedNumberConfigurations;
  }

  get phoneNumberOptions() {
    let defaultItems = [
      {
        text: this.intl.t('ai-agent.voice-playground.default'),
        icon: 'play',
        isSelected: !this.args.selectedNumberConfigurationPhoneNumber,
        onSelectItem: () => {
          this.args.selectConfig(undefined);
        },
      },
    ];

    let phoneNumberItems = Object.entries(this.numberConfigurations).map(([phoneNumber, _]) => ({
      phoneNumber,
      phoneNumberName: this.phoneNumbers[phoneNumber].name,
      isSelected: this.args.selectedNumberConfigurationPhoneNumber === phoneNumber,
      component: ConnectButtonDropdownItem,
      onSelectItem: () => {
        this.args.selectConfig(phoneNumber);
      },
    }));

    let groups: {
      groupTitle: string;
      items: any[];
    }[] = [
      {
        groupTitle: this.intl.t('ai-agent.voice-playground.default-options'),
        items: defaultItems,
      },
    ];

    if (phoneNumberItems.length) {
      groups.push({
        groupTitle: this.intl.t('ai-agent.voice-playground.phone-numbers'),
        items: phoneNumberItems,
      });
    }

    return groups;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::ConnectButton': typeof ConnectButton;
  }
}
